import { ModalContent, ModalGrid, ModalHeader } from 'components/GlobalModal';
import queryKeys from 'config/queryKeys';
import KoalaButton from 'koala/components/Button';
import KoalaIconButton from 'koala/components/IconButton';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { setGlobalModalContent } from 'state/actions/globalUIActions';
import styled from 'styled-components';
import theme from 'theme';
import { CustomTermKey, translate } from 'utils/customTermUtils';
import * as remoteApi from 'api/remote';
import { format } from 'date-fns';
import download from 'downloadjs';
import { Plan } from 'types';
import ExportPlanPowerPoint from './ExportPlanPowerPoint';
import ExcelIcon from './excel_icon.png';
import GoogleSlidesIcon from './google_slides_icon.png';
import PowerPointIcon from './powerpoint_icon.png';
import GoogleSheetsIcon from './google_sheets_icon.png';

const Section = styled.div`
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
  padding: 20px;
  border-radius: 10px;
  display: flex;
  gap: ${theme.spacing.x1};
  justify-content: space-between;
  > div {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x1};
    &:last-of-type {
      flex-direction: row;
      gap: ${theme.spacing.x2};
    }
    img {
      height: 4.8rem;
    }
`;

interface Props {
  planId: string;
}

function ExportPlan(props: Props) {
  const { planId } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentWorkspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);
  const translationKey = 'modals.exportPlan';
  const [sheetResult, setSheetResult] = useState('');
  const [sheetPresentationUrl, setSheetPresentationUrl] = useState('');
  const [csvResult, setCsvResult] = useState('');

  const queryKey = [queryKeys.integrations, currentWorkspace.slug];
  const staleTime = queryKeys.staleTime;
  const { data: integrationData } = useQuery(queryKey, remoteApi.fetchIntegrations, {
    staleTime,
  });

  const queryKeyPlan = [queryKeys.currentPlan, planId];

  const { data: planData } = useQuery(queryKeyPlan, remoteApi.fetchPlanDetails, {
    staleTime,
  });

  const plan: Plan = planData?.data;

  const [exportPlanSlidesMutation, { isLoading: isExportingSlides }] = useMutation(remoteApi.exportPlanSlides, {
    onSuccess: (response) => {
      const presentationURL = `https://docs.google.com/presentation/d/${response.data.presentation_id}/edit`;
      const sheetMessage = t('modals.exportPlan.slideSuccess');
      setSheetResult(sheetMessage);
      setSheetPresentationUrl(presentationURL);
    },
    onError: (error) => {
      const sheetMessage = t('modals.exportPlan.slideError');
      setSheetResult(sheetMessage);
      setSheetPresentationUrl('');
    },
  });

  // Mutation that will create the csv in the backend
  const [exportPlanCSVMutation, { isLoading: isExportingCSV }] = useMutation(remoteApi.exportPlanCSV, {
    onSuccess: (response: any) => {
      const content = response.headers['content-type'];
      const fileName = `${plan.title}-${format(new Date(), 'Y-MM-dd')}.csv`;
      download(response.data, fileName, content);
      const csvMessage = t('modals.exportPlan.csvSuccess');
      setCsvResult(csvMessage);
    },
    onError: (error) => {
      const csvMessage = t('modals.exportPlan.csvError');
      setCsvResult(csvMessage);
    },
  });

  const integrations = integrationData?.data;

  const handleCancel = () => {
    dispatch(setGlobalModalContent(''));
  };

  const handleExportSlides = () => {
    setSheetResult('');
    setCsvResult('');
    const params = {
      planId,
    };
    exportPlanSlidesMutation(params);
  };

  const handleExportCSV = () => {
    setSheetResult('');
    setCsvResult('');
    const mutationParams = {
      workspaceSlug: currentWorkspace.slug,
      planId: plan.nano_slug,
    };
    exportPlanCSVMutation(mutationParams);
  };

  const hasGoogleIntegration = integrations.some((integration: any) => integration.integration_type === 'google');

  const isExporting = isExportingSlides || isExportingCSV;
  return (
    <ModalGrid>
      <ModalHeader>
        <h2>{t(`${translationKey}.title`, { label: translate(currentWorkspace, CustomTermKey.INITIATIVE, 2) })}</h2>
        <KoalaIconButton onClick={handleCancel} iconName="close" />
      </ModalHeader>
      <ModalContent>
        <Section>
          <div>
            <h3>{t(`${translationKey}.csvTitle`)}</h3>

            <p>{t(`${translationKey}.csvDescription`)}</p>

            <div>
              <KoalaButton
                onClick={handleExportCSV}
                appearance="subtle"
                loading={isExportingCSV}
                disabled={isExporting}
              >
                {t(`${translationKey}.button`)}
              </KoalaButton>
            </div>
            {csvResult && <small>{csvResult}</small>}
          </div>
          <div>
            <img src={ExcelIcon} alt="Excel" />
            <img src={GoogleSheetsIcon} alt="Google Sheets" />
          </div>
        </Section>
        <Section>
          <div>
            <h3>{t(`${translationKey}.slideTitle`)}</h3>
            {!hasGoogleIntegration && (
              <>
                <p>{t(`${translationKey}.connectAccount`)}</p>
                <div>
                  <KoalaButton
                    to={`/${currentWorkspace.slug}/account/integrations`}
                    onClick={handleCancel}
                    isLink
                    appearance="subtle"
                  >
                    {t(`${translationKey}.connectTo`)}
                  </KoalaButton>
                </div>
              </>
            )}
            {hasGoogleIntegration && (
              <>
                <p>{t(`${translationKey}.slideDescription`)}</p>

                <div>
                  <KoalaButton
                    onClick={handleExportSlides}
                    appearance="subtle"
                    loading={isExportingSlides}
                    disabled={isExporting}
                  >
                    {t(`${translationKey}.button`)}
                  </KoalaButton>
                </div>
              </>
            )}
            {sheetResult && (
              <small>
                {sheetResult}&nbsp;
                {sheetPresentationUrl && (
                  <a
                    href={sheetPresentationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline' }}
                  >
                    {t(`${translationKey}.openInGoogleSlides`)}
                  </a>
                )}
              </small>
            )}
          </div>
          <div>
            <img src={GoogleSlidesIcon} alt="Google Slides" />
          </div>
        </Section>
        <Section>
          <div>
            <h3>{t(`${translationKey}.pptTitle`)}</h3>
            <p>{t(`${translationKey}.pptDescription`)}</p>
            {plan && <ExportPlanPowerPoint plan={plan} />}
          </div>
          <div>
            <img src={PowerPointIcon} alt="PowerPoint" />
          </div>
        </Section>
      </ModalContent>
    </ModalGrid>
  );
}

export default ExportPlan;
